import { parseUrlParams, parseStartParam } from '../functions/startParam'

const BROWSER_REF_KEY = 'browser_referral_key'
const BROWSER_REF_TIMESTAMP_KEY = 'browser_referral_key_timestamp'
const REF_EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

// Referral key validation: alphanumeric, underscore, hyphen, 1-50 chars
const VALID_REF_PATTERN = /^[a-zA-Z0-9_-]{1,50}$/

/**
 * Validates referral key format to prevent XSS and localStorage pollution.
 */
const isValidReferralKey = (ref: string | null | undefined): ref is string => {
  return (
    typeof ref === 'string' && ref.length > 0 && VALID_REF_PATTERN.test(ref)
  )
}

/**
 * Safe localStorage getter that handles errors in incognito/storage-disabled browsers.
 */
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Safe localStorage setter that handles errors in incognito/storage-disabled browsers.
 */
const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Ignore storage errors (incognito mode, quota exceeded, etc.)
  }
}

/**
 * Safe localStorage remover that handles errors in incognito/storage-disabled browsers.
 */
const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore storage errors
  }
}

/**
 * Captures referral key from browser URL or Telegram startParam.
 * Persists to localStorage so it survives page refresh before wallet connection.
 * Stored ref expires after 7 days to prevent stale refs from being used.
 *
 * Priority: Telegram startParam > Browser URL > localStorage (if not expired)
 */
export const captureReferralKey = (): string | null => {
  // Check browser URL params (captured early or current)
  const capturedRef = window.__INITIAL_URL_PARAMS__?.ref
  const urlRef = parseUrlParams().ref
  const browserRef = capturedRef ?? urlRef

  // Check Telegram startParam (E2E mock or real Telegram WebApp)
  const tgStartParam =
    window.__E2E_TELEGRAM_START_PARAM__ ??
    window.Telegram.WebApp.initDataUnsafe.start_param
  const { ref: telegramRef } = parseStartParam(tgStartParam)

  // Telegram ref takes priority, then browser ref
  const ref = telegramRef ?? browserRef

  // Validate ref format before storing
  if (isValidReferralKey(ref)) {
    // Save to localStorage with timestamp for expiration check
    safeSetItem(BROWSER_REF_KEY, ref)
    safeSetItem(BROWSER_REF_TIMESTAMP_KEY, Date.now().toString())
    return ref
  }

  // If no valid ref found, check localStorage (user might have refreshed)
  const storedRef = safeGetItem(BROWSER_REF_KEY)
  if (isValidReferralKey(storedRef)) {
    // Check if stored ref has expired
    const timestamp = safeGetItem(BROWSER_REF_TIMESTAMP_KEY)
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp, 10)
      if (age > REF_EXPIRATION_MS) {
        // Ref has expired, clear it
        clearStoredReferralKey()
        return null
      }
    }
    return storedRef
  }

  return null
}

/**
 * Clears the stored referral key and timestamp from localStorage.
 * Should be called after successful authentication.
 */
export const clearStoredReferralKey = (): void => {
  safeRemoveItem(BROWSER_REF_KEY)
  safeRemoveItem(BROWSER_REF_TIMESTAMP_KEY)
}
